import clsx from "clsx";
import { type UseFormReturn, useController } from "react-hook-form";

import RenderIf from "common/components/RenderIf";
import { TEMPLATE_TYPES } from "common/constants";
import { getSchemaMaxLength } from "common/helpers/utils";
import type { Template } from "common/types";

import TemplateMessageEditor from "./TemplateMessageEditor";
import TemplateSubjectInput from "./TemplateSubjectInput";

import type { JSX } from "react";

export default function TemplateEditor({
  form,
  isEditable,
  placeholderText = null,
  customVariables = [],
  autoFocus = false,
  languageDropdownComponent = null,
}: {
  form: UseFormReturn<Template>;
  isEditable: boolean;
  placeholderText?: string;
  customVariables?: { name: string; value: string }[];
  autoFocus?: boolean;
  languageDropdownComponent?: JSX.Element;
}) {
  const {
    getValues,
    control,
    watch,
    formState: { errors },
  } = form;

  const templateType = getValues("type");

  const { field: messageField } = useController({
    name: "message",
    control,
  });

  const { field: aiDescriptorsField } = useController({
    name: "ai_descriptors",
    control,
  });

  const { flowValidationSchema } = TEMPLATE_TYPES[templateType];

  const templateMaxLength = getSchemaMaxLength(
    flowValidationSchema.entries.message,
  );

  const hasError = errors.message || errors.ai_descriptors;

  return (
    <section
      className={clsx(
        "flex flex-1 flex-col overflow-hidden rounded-3xl border bg-white outline-2 transition-[border] max-lg:max-h-[50dvh] lg:basis-1/2",
        hasError
          ? "border-red-500 has-[:focus-visible]:outline has-[:focus-visible]:outline-red-300"
          : "border-black-200 has-[:focus-visible]:border-purple-400",
      )}
    >
      {placeholderText ? (
        <p className="text-black-400">
          Select a template first. The text will show a preview here when
          selected
        </p>
      ) : (
        <>
          <RenderIf condition={templateType === "INMAIL_TEMPLATE"}>
            <TemplateSubjectInput
              disabled={!isEditable}
              characterLength={watch("subject")?.length}
              errorMessage={"subject" in errors && errors.subject.message}
              {...form.register("subject")}
            />
          </RenderIf>
          <TemplateMessageEditor
            initialData={getValues()}
            onChange={({ message, ai_descriptors }) => {
              messageField.onChange(message);
              aiDescriptorsField.onChange(ai_descriptors);
            }}
            isEditable={isEditable}
            customVariables={customVariables}
            messageMaxLength={templateMaxLength}
            autoFocus={autoFocus}
            languageDropdownComponent={languageDropdownComponent}
          />
        </>
      )}
    </section>
  );
}
